import type { supplierContract } from '@montugroup/contracts-warehousing-api';
import type { ClientInferResponseBody } from '@ts-rest/core';
import { z } from 'zod';
import type { B2CApiResponseType } from './B2COrdersData';

type TableOrder = 'asc' | 'desc';

type OrdersB2BResponseType = ClientInferResponseBody<typeof supplierContract.listOrderItems, 200>;
type OrdersB2BResponseDataItemType = OrdersB2BResponseType['data'][number];

/**
 * Type guard to check if the response is a B2B response.
 * Checks if the response has the expected structure for a B2B order response,
 * which includes a 'data' array containing objects with an 'object' property set to 'supplier-order-item'.
 * @param response - the response to check
 * @returns - true if the response is a B2B response, false otherwise.
 */
const isB2BResponse = (response: unknown): response is OrdersB2BResponseType => {
  if (
    !response ||
    typeof response !== 'object' ||
    !('data' in response) ||
    !('meta' in response) ||
    !Array.isArray(response.data) ||
    typeof response.meta !== 'object'
  ) {
    return false;
  }

  if (response.data.length === 0) {
    return true; // An empty data array is still considered a valid B2B response
  }

  const firstItem = response.data[0];
  return (
    firstItem !== null &&
    typeof firstItem === 'object' &&
    'object' in firstItem &&
    firstItem.object === 'supplier-order-item'
  );
};

type OrdersResponseDataType = B2CApiResponseType | OrdersB2BResponseType;

const OrdersTableStatusSchema = z.enum(['all', 'awaitingApproval', 'approved', 'rejected']);
type OrdersTableStatusType = z.infer<typeof OrdersTableStatusSchema>;

const OrdersTableRowSchema = z.object({
  id: z.string(),
  product: z.string(),
  sku: z.string(),
  quantity: z.number(),
  orderDate: z.string(),
  pharmacy: z.string(),
  address: z.string().optional(),
  contact: z.string().optional(),
  approval: z
    .object({
      id: z.string(),
      documentPath: z.string().optional(),
    })
    .optional(),
  doctor: z.string().optional(),
  status: z.string(),
  dispatch: z.string().nullable(),
});
type OrdersTableRowType = z.infer<typeof OrdersTableRowSchema>;

const OrdersRowKeysSchema = z.custom<keyof OrdersTableRowType>((value) => {
  return Object.keys(OrdersTableRowSchema.shape).includes(value);
});
type OrdersRowKeysType = z.infer<typeof OrdersRowKeysSchema>;

const OrdersTableHeadCellSchema = z.object({
  id: OrdersRowKeysSchema,
  label: z.string(),
  visible: z.boolean().optional(),
});
type OrdersTableHeadCellType = z.infer<typeof OrdersTableHeadCellSchema>;

const OrdersTableDataSchema = z.object({
  type: z.enum(['b2b', 'b2c', 'replenishment']),
  rows: z.array(OrdersTableRowSchema),
});
type OrdersTableDataType = z.infer<typeof OrdersTableDataSchema>;

export {
  isB2BResponse,
  OrdersRowKeysSchema,
  OrdersTableDataSchema,
  OrdersTableHeadCellSchema,
  OrdersTableRowSchema,
  OrdersTableStatusSchema,
};

export type {
  OrdersB2BResponseDataItemType,
  OrdersB2BResponseType,
  OrdersResponseDataType,
  OrdersRowKeysType,
  OrdersTableDataType,
  OrdersTableHeadCellType,
  OrdersTableRowType,
  OrdersTableStatusType,
  TableOrder,
};
